/**
 * OHIF viewer route names — the single source of truth for `/ohif/<path>` URLs.
 *
 * A mode's URL segment is its **`routeName`**, not its package name and not the
 * `path` in its own `routes[]` array — OHIF's route builder does
 * `` `/${mode.routeName}` ``. Upstream's read-only mode is packaged as
 * `@ohif/mode-longitudinal` but sets `routeName: 'viewer'`, so `/ohif/longitudinal`
 * matches no route: nginx's SPA fallback serves index.html and the tab renders
 * blank with no network error to point at. That's the bug this file exists to
 * prevent — the wrong name was hardcoded in five places.
 *
 * Verify against a built image rather than the mode source:
 *   docker compose exec tagging-ui sh -c \
 *     'grep -ho "routeName:\"[a-z-]*\"" /usr/share/nginx/html/ohif/*.js | sort -u'
 */
export const OHIF_ROUTE = {
  /**
   * "Read-only" viewer route — where the radiologist's "Open viewer" button
   * lands, and where any QA/re-view path defaults. Nominally the upstream
   * Basic mode (`@ohif/mode-longitudinal`, routeName `viewer`), but v3.13's
   * basic mode uses a stack viewport and its SEG hydration on that path
   * differs from v3.10 — our auto-hydrate in `ohif-config/medimaging.js`
   * doesn't cover the stack path cleanly, so the mask overlay stays blank.
   *
   * `tagging` (MPR/volume viewport, mode-tagging) renders the labelmap SEG
   * correctly. It's the technician's editor by design, but the mask
   * mutability lock is enforced server-side, not UI-side — a radiologist
   * hits `POST /segmentations/...` and the gateway 403s because their role
   * has no `SEG_WRITE`. So pointing "Open viewer" at `/ohif/tagging` gives
   * radiologists the mask overlay they need while making mask edits
   * impossible to persist. The alternative was a v3.13-basic-mode SEG
   * hydration rewrite, which is far more invasive than this one-line switch.
   *
   * Revert to `'viewer'` once basic-mode SEG hydration in v3.13 is fixed.
   */
  basic: 'tagging',
  /**
   * "Plain" basic viewer — upstream `@ohif/mode-longitudinal`, routeName
   * `viewer`. Used specifically when we want the CT WITHOUT any mask
   * overlay (e.g. the radiologist's anomaly-annotation "Open viewer",
   * so a technician's in-progress mask edits don't bias the read).
   * The comment on `basic` above explains why v3.13's basic-mode SEG
   * hydration is unreliable — here that unreliability is exactly what we
   * want: no SEG hydration means no mask overlay.
   */
  basicPlain: 'viewer',
  /** `@medimaging/mode-tagging` — label-map + contour editor for mask work. */
  tagging: 'tagging',
  /** `@medimaging/mode-anomaly` — label-map + contour editor for the anomaly read. */
  anomaly: 'anomaly',
  /** `@medimaging/mode-ccta` — vessel editor (Lumen/Wall + centerline). */
  ccta: 'ccta',
  /** Same mode, opened read-only for the vessel review. */
  cctaReadOnly: 'ccta',
} as const

export type OhifRouteKey = keyof typeof OHIF_ROUTE

/**
 * Routes that must open without any editing affordance.
 *
 * `basic` points at the `tagging` mode (see above) because that is the only mode
 * in v3.13 that renders the SEG overlay — but `basic` is where every *review*
 * and *check* flow lands, and handing a reviewer a full label-map editor is
 * wrong even when the server would reject the write. `readOnly=1` makes
 * `mode-tagging` register its editing tools as `disabled` and drop them from the
 * toolbar.
 *
 * This is a UX guard on top of real enforcement, exactly like `RequireRole` is
 * for routes: the mask lock (`_assert_mask_writable`) and `radiologist` having no
 * `SEG_WRITE` are what actually make the mask immutable.
 *
 * `tagging` and `anomaly` are authoring routes and are never read-only.
 * `basicPlain` needs no flag — it has no segmentation extension at all.
 */
const READ_ONLY_ROUTES: ReadonlySet<OhifRouteKey> = new Set<OhifRouteKey>(['basic', 'cctaReadOnly'])

/** `/ohif/<route>?StudyInstanceUIDs=…` plus any extra breadcrumb params. */
export function ohifUrl(
  route: OhifRouteKey,
  studyUid: string,
  extra?: Record<string, string>,
): string {
  const params = new URLSearchParams({
    StudyInstanceUIDs: studyUid,
    ...(READ_ONLY_ROUTES.has(route) ? { readOnly: '1' } : {}),
    ...extra,
  })
  return `/ohif/${OHIF_ROUTE[route]}?${params.toString()}`
}
